import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Snackbar,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { RHFRadioGroup } from "../../hook-form/RHFRadioGroup";
import { RHFTextField } from "../../hook-form/RHFTextField";
import { RHFCheckbox } from "../../hook-form/RHFCheckbox";
import { postStringPeel } from "../../../services/stringPeelService";
import { StringPeelPostPayload } from "../../../types/stringPeelTypes";

type Props = {
  stationId: number;
  onSuccess?: () => void;
  onError?: (error: string) => void;
};

type FormValues = {
  adhesiveForceFront: string;
  adhesiveForceBack: string;
  lubricationFront: string;
  lubricationBack: string;
  isSiliciumBreakoutsFront: boolean;
  isSiliciumBreakoutsBack: boolean;
  isGood: boolean;
  manufacturingLine: "A" | "B" | "";
  notes: string;
};

function parseRange(input: string): { min: number; max: number } | null {
  const trimmed = (input || "").trim();
  if (!trimmed.includes("-")) return null;
  const [a, b] = trimmed.split("-").map((s) => parseFloat(s));
  if (isNaN(a) || isNaN(b)) return null;
  return { min: a, max: b };
}

export function StringPeelForm({ stationId, onSuccess, onError }: Props) {
  const methods = useForm<FormValues>({
    defaultValues: {
      adhesiveForceFront: "",
      adhesiveForceBack: "",
      lubricationFront: "",
      lubricationBack: "",
      isSiliciumBreakoutsFront: false,
      isSiliciumBreakoutsBack: false,
      isGood: false,
      manufacturingLine: "",
      notes: "",
    },
  });

  const { control, handleSubmit, reset, setError, clearErrors } = methods;
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState<null | string>(null);
  const [warningOpen, setWarningOpen] = useState<null | string>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState<
    null | (() => Promise<void>)
  >(null);

  const lines = useMemo(
    () => [
      { label: "Linie A", value: "A" },
      { label: "Linie B", value: "B" },
    ],
    []
  );

  const onSubmit = async (data: FormValues) => {
    if (
      !data.adhesiveForceFront ||
      !data.adhesiveForceBack ||
      !data.lubricationFront ||
      !data.lubricationBack ||
      !data.manufacturingLine
    ) {
      setWarningOpen("Bitte füllen Sie alle Felder aus!");
      return;
    }

    const front = parseRange(data.adhesiveForceFront);
    const back = parseRange(data.adhesiveForceBack);

    let hasError = false;
    if (!front) {
      setError("adhesiveForceFront", {
        type: "validate",
        message: "Format: min-max (z. B. 1.1-1.3)",
      });
      hasError = true;
    }
    if (!back) {
      setError("adhesiveForceBack", {
        type: "validate",
        message: "Format: min-max (z. B. 2-3)",
      });
      hasError = true;
    }
    if (hasError) return;

    clearErrors(["adhesiveForceFront", "adhesiveForceBack"]);

    let relationshipError = false;
    if (front!.min >= front!.max) {
      setError("adhesiveForceFront", {
        type: "validate",
        message: "Min muss kleiner als Max sein und nicht gleich.",
      });
      relationshipError = true;
    }
    if (back!.min >= back!.max) {
      setError("adhesiveForceBack", {
        type: "validate",
        message: "Min muss kleiner als Max sein und nicht gleich.",
      });
      relationshipError = true;
    }
    if (relationshipError) return;

    if (!data.isGood) {
      setConfirmOpen(true);
      setPendingSubmit(() => async () => {
        await performSubmit(data, front!, back!);
      });
      return;
    }

    await performSubmit(data, front!, back!);
  };

  const performSubmit = async (
    data: FormValues,
    front: { min: number; max: number },
    back: { min: number; max: number }
  ) => {
    const payload: StringPeelPostPayload = {
      stationid: stationId,
      dateInProduction: new Date(),
      adhesiveForceFrontMin: front.min,
      adhesiveForceFrontMax: front.max,
      adhesiveForceBackMin: back.min,
      adhesiveForceBackMax: back.max,
      lubricationFront: parseInt(data.lubricationFront, 10),
      lubricationBack: parseInt(data.lubricationBack, 10),
      isSiliciumBreakoutsFront: data.isSiliciumBreakoutsFront,
      isSiliciumBreakoutsBack: data.isSiliciumBreakoutsBack,
      isGood: data.isGood,
      manufacturingLine: data.manufacturingLine,
      notes: data.notes,
    };

    try {
      await postStringPeel(payload);
      reset();
      setSuccessOpen(true);
      onSuccess?.();
    } catch (e: any) {
      if (e.code === "ERR_CANCELED" || e.name === "CanceledError") {
        console.debug("StringPeel request canceled");
        return;
      }
      setErrorOpen(e?.message || "Ein Fehler ist aufgetreten");
      onError?.(e?.message || "Ein Fehler ist aufgetreten");
    }
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} p={2}>
        <Stack spacing={2}>
          {/* Adhesive forces */}
          <RHFTextField
            name="adhesiveForceFront"
            label="Haftkraft Vorderseite [N]"
            control={control}
            placeholder="1.1-1.3"
            required
          />
          <RHFTextField
            name="adhesiveForceBack"
            label="Haftkraft Hinterseite [N]"
            control={control}
            placeholder="2-3"
            required
          />

          {/* Lubrication */}
          <RHFTextField
            name="lubricationFront"
            label="Benetzung Vorderseite [%]"
            control={control}
            placeholder="80"
            required
          />
          <RHFTextField
            name="lubricationBack"
            label="Benetzung Hinterseite [%]"
            control={control}
            placeholder="95"
            required
          />

          {/* Checkboxes */}
          <RHFCheckbox
            name="isSiliciumBreakoutsFront"
            label="Siliziumausbrüche Vorderseite?"
            control={control}
          />
          <RHFCheckbox
            name="isSiliciumBreakoutsBack"
            label="Siliziumausbrüche Hinterseite?"
            control={control}
          />
          <RHFCheckbox
            name="isGood"
            label="Gesamtergebnis i.O.?"
            control={control}
          />

          {/* Production line */}
          <RHFRadioGroup
            name="manufacturingLine"
            label="Produktionslinie"
            control={control}
            options={lines}
            row
            required
          />

          {/* Notes */}
          <RHFTextField
            name="notes"
            label="Bemerkungen"
            control={control}
            placeholder="Silberpastenausbruch"
          />

          <Button type="submit" variant="contained" color="primary">
            Speichern
          </Button>
        </Stack>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={successOpen}
        autoHideDuration={6000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Der Eintrag wurde gespeichert!
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorOpen}
        autoHideDuration={6000}
        onClose={() => setErrorOpen(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorOpen(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorOpen}
        </Alert>
      </Snackbar>

      {/* Warning Snackbar */}
      <Snackbar
        open={!!warningOpen}
        autoHideDuration={6000}
        onClose={() => setWarningOpen(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setWarningOpen(null)}
          severity="warning"
          sx={{ width: "100%" }}
        >
          {warningOpen}
        </Alert>
      </Snackbar>

      {/* Confirm dialog when not everything is good */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Gesamtergebnis n.i.O.</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Das Gesamtergebnis ist n.i.O.. Trotzdem speichern?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="inherit">
            Abbrechen
          </Button>
          <Button
            id="success"
            color="success"
            variant="contained"
            onClick={async () => {
              setConfirmOpen(false);
              const action = pendingSubmit;
              setPendingSubmit(null);
              if (action) await action();
            }}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
